/**
 * The Statistics panel (Window › Statistics), like Unreal's: what the open
 * level's assets cost, one row per asset it references, sortable by any
 * column, with totals. Figures come from the content index (read from the
 * files at build time), so the panel never queries the renderer; texture
 * memory is labeled an estimate, since browsers don't expose GPU memory.
 */
import { useState } from "react";
import { formatBytes } from "../../assets/AssetRegistry";
import { useEditor } from "../EditorContext";
import { useWorldSnapshot } from "../hooks/editorHooks";
import { assetsInUse, sortRows, statisticsRows, totalsOf } from "./statisticsModel";
import type { AssetRow, SortDirection, SortKey } from "./statisticsModel";
import "./statistics.css";

interface Column {
  readonly key: SortKey;
  readonly label: string;
  readonly format: (row: Pick<AssetRow, SortKey>) => string;
  /** Totals show for summable columns; the name and use count have none. */
  readonly total: boolean;
}

const count = (n: number) => n.toLocaleString("en-US");

const COLUMNS: readonly Column[] = [
  { key: "name", label: "Asset", format: (row) => row.name, total: false },
  { key: "uses", label: "Used by", format: (row) => count(row.uses), total: false },
  {
    key: "downloadBytes",
    label: "Download",
    format: (row) => formatBytes(row.downloadBytes),
    total: true,
  },
  { key: "triangles", label: "Triangles", format: (row) => count(row.triangles), total: true },
  { key: "vertices", label: "Vertices", format: (row) => count(row.vertices), total: true },
  {
    key: "geometryBytes",
    label: "Geometry",
    format: (row) => formatBytes(row.geometryBytes),
    total: true,
  },
  {
    key: "textureBytes",
    label: "Textures (est.)",
    format: (row) => formatBytes(row.textureBytes),
    total: true,
  },
];

export function StatisticsPanel() {
  const { components, content } = useEditor();
  const { entities } = useWorldSnapshot();
  const [sort, setSort] = useState<{ key: SortKey; direction: SortDirection }>({
    key: "downloadBytes",
    direction: "descending",
  });

  const rows = sortRows(
    statisticsRows(content.index.assets, assetsInUse(entities, components)),
    sort.key,
    sort.direction,
  );

  if (rows.length === 0) {
    return (
      <div className="kn-statistics kn-statistics--empty">The open level references no assets.</div>
    );
  }

  // A header click sorts by that column, largest first; a second click reverses it.
  const sortBy = (key: SortKey) => {
    setSort((current) => ({
      key,
      direction:
        current.key === key && current.direction === "descending" ? "ascending" : "descending",
    }));
  };
  const totals = totalsOf(rows);

  return (
    <div className="kn-statistics">
      <table className="kn-statistics__table" aria-label="Level assets">
        <thead>
          <tr>
            {COLUMNS.map((column) => (
              <th
                key={column.key}
                scope="col"
                aria-sort={sort.key === column.key ? sort.direction : "none"}
              >
                <button
                  type="button"
                  onClick={() => {
                    sortBy(column.key);
                  }}
                >
                  {column.label}
                </button>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.path} title={row.path}>
              {COLUMNS.map((column) => (
                <td key={column.key}>{column.format(row)}</td>
              ))}
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            {COLUMNS.map((column, index) => (
              <td key={column.key}>
                {index === 0
                  ? "Total"
                  : column.total
                    ? column.format({ ...totals, name: "", uses: 0 })
                    : ""}
              </td>
            ))}
          </tr>
        </tfoot>
      </table>
      <p className="kn-statistics__note">
        Estimated from asset files (textures as RGBA8, mipmapped)
      </p>
    </div>
  );
}
